import { brainFactsSection } from '@/content/homepage';

/**
 * "The Reality of Brain Cancer" — comparison stats, why progress stalled,
 * and structural barriers. Drawn from the briefing mockup content.
 */
export function BrainFacts() {
  const s = brainFactsSection;

  return (
    <section
      id={s.id}
      aria-labelledby="brain-facts-heading"
      className="scroll-mt-24 border-border/70 border-y bg-card/40 py-20 sm:py-24"
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-primary mb-4 text-xs font-semibold tracking-[0.24em] uppercase">
            {s.eyebrow}
          </p>
          <h2
            id="brain-facts-heading"
            className="font-display text-foreground text-3xl font-semibold tracking-tight text-balance sm:text-4xl"
          >
            {s.heading}
          </h2>
          <p className="text-muted-foreground mt-5 text-base leading-relaxed sm:text-lg">
            {s.lead}
          </p>
        </div>

        {/* Comparison stats */}
        <div className="mt-16">
          <h3 className="font-display text-foreground mb-8 text-xl font-semibold tracking-tight sm:text-2xl">
            {s.comparisonHeading}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {s.comparisonStats.map((stat) => (
              <div
                key={stat.label}
                className={
                  stat.isBrainCancer
                    ? 'border-cta/40 bg-cta/5 rounded-2xl border p-6 text-center'
                    : 'border-border/60 bg-background/40 rounded-2xl border p-6 text-center'
                }
              >
                <div
                  className={
                    stat.isBrainCancer
                      ? 'font-display text-cta text-4xl font-semibold tracking-tight tabular-nums sm:text-5xl'
                      : 'font-display text-muted-foreground text-4xl font-semibold tracking-tight tabular-nums sm:text-5xl'
                  }
                >
                  {stat.value}
                </div>
                <p
                  className={
                    stat.isBrainCancer
                      ? 'text-foreground mt-3 text-sm font-medium leading-relaxed'
                      : 'text-muted-foreground mt-3 text-sm leading-relaxed'
                  }
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground mt-4 text-center text-xs leading-relaxed sm:text-sm">
            {s.comparisonCaption}
          </p>
        </div>

        {/* Why progress has stalled */}
        <div className="mt-16">
          <h3 className="font-display text-foreground mb-8 text-xl font-semibold tracking-tight sm:text-2xl">
            {s.stalledHeading}
          </h3>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {s.stallReasons.map((reason) => (
              <article
                key={reason.title}
                className="border-border/70 bg-background/60 rounded-2xl border p-6"
              >
                <h4 className="font-display text-foreground text-base font-semibold tracking-tight">
                  {reason.title}
                </h4>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                  {reason.body}
                </p>
              </article>
            ))}
          </div>
        </div>

        {/* Structural barriers */}
        <div className="mt-16">
          <h3 className="font-display text-foreground mb-6 text-xl font-semibold tracking-tight sm:text-2xl">
            {s.barriersHeading}
          </h3>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {s.barriers.map((barrier) => (
              <li
                key={barrier}
                className="text-muted-foreground border-border/60 border-l-2 pl-4 text-sm leading-relaxed"
              >
                {barrier}
              </li>
            ))}
          </ul>
        </div>

        {/* Conclusion callout */}
        <div className="border-l-cta/60 bg-cta/5 mt-16 border-l-4 rounded-r-xl p-6 sm:p-8">
          <p className="text-foreground text-base leading-relaxed sm:text-lg">
            {s.conclusion}
          </p>
        </div>
      </div>
    </section>
  );
}
